import React, { createContext, useContext, useEffect, useState } from 'react';
import fetchUserRolesPrivileges from '../components/UserRoles/UserRolesPrivileges';
import { tokenManager } from '../utils/apiClient';
import { toast } from '../components/common';

// Create the context
const UserContext = createContext();

// Create a custom hook for using the user context
export const useUser = () => useContext(UserContext);

// UserProvider component
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [roles, setRoles] = useState([]);
  const [privileges, setPrivileges] = useState([]);

  // Handle token expiration event
  useEffect(() => {
    const handleTokenExpired = () => {
      console.log('JWT token expired, logging out user');

      // Show user-friendly notification
      toast.warning('Session Expired', 'Your session has expired due to inactivity. Please log in again.');

      logoutUser();
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);

    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, []);

  const loginUser = async (userData, accessToken, refreshToken) => {
    try {
      setUser(userData);
      setIsLoggedIn(true);

      // Store JWT tokens if provided
      if (accessToken && refreshToken) {
        tokenManager.setTokens(accessToken, refreshToken);
      }

      await loadUserRolesPrivileges();
      return true; // Return true on successful login
    } catch (error) {
      console.error('Login error:', error);
      return false; // Return false on error
    }
  };

  const loadUserRolesPrivileges = async () => {
    const {roles, privileges} = await fetchUserRolesPrivileges();
    setRoles(roles);
    setPrivileges(privileges);
  };

  // Function to log out the user
  const logoutUser = () => {
    setUser(null);
    setIsLoggedIn(false);
    setRoles([]);
    setPrivileges([]);

    // Clear JWT tokens from localStorage
    tokenManager.clearTokens();

    // SECURITY: Clear all TrustGrid cached data on logout to prevent cross-org data leakage
    // This ensures next user doesn't see cached projects from previous user
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('trustgrid_') || key.startsWith('pinnedFrameworks_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // SECURITY: Clear all sessionStorage data on logout
    // This includes Test Case Generator uploaded files and other session-specific data
    sessionStorage.clear();
  };

  // Function to update user data
  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <UserContext.Provider value={{ user, isLoggedIn, loginUser, logoutUser, updateUser, roles, privileges }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
